<script setup lang="ts">
import { useRouteQuery } from '@vueuse/router'

const { ecommerce } = useAnalytics()
const router = useRouter()
const route = useRoute()
const { el: headerRef, isHeaderVisible } = useUiHeader()
const isSearchPage = computed(() => ((route?.name as string) ?? '').includes('search'))
const isCheckoutPage = computed(() => ((route?.name as string) ?? '').includes('checkout'))
const drawers = useDrawers()
const { t } = useI18n()
const localePath = useLocalePath()
const { navigation: accountNav } = useAccount()

const { state: rootCollectionsState } = useRootCollectionsQuery()

const loggedIn = computed(() => useAuth().loggedIn.value === true)

const searchQuery = useRouteQuery('q', '')
const searchQueryInput = ref(searchQuery.value)

watch(isSearchPage, (isSearchPage) => {
  if (!isSearchPage)
    searchQueryInput.value = ''
})

const openDropdownMenu = ref(false)
function onDropdownMenuOpen(value: boolean) {
  if (!loggedIn.value)
    return router.push(localePath('/account'))
  openDropdownMenu.value = value
}

function handleNavigationClick() {
  drawers.value.navigation.open = true
}

async function handleCartClick() {
  drawers.value.cart.open = true
  ecommerce.viewCart()
}

function submitSearch(event: Event) {
  event.preventDefault()
  ecommerce.search(searchQueryInput.value)
  router.push({
    path: localePath('/search'),
    query: { q: searchQueryInput.value },
  })
}

// const { state: _order } = useOrder()
const { activeOrder } = useActiveOrderQuery()
const totalQuantity = computed(() => activeOrder.value.data?.totalQuantity ?? 0)
</script>

<template>
  <Header ref="headerRef" :visible="isHeaderVisible">
    <template #logo>
      <div class="block -ml-4 lg:hidden">
        <UiButton
          v-if="!isCheckoutPage"
          id="navigation"
          :aria-label="t('general.navigation.label')"
          variant="ghost"
          size="icon"
          @click="handleNavigationClick"
        >
          <Icon name="lucide:menu" class="text-xl" />
        </UiButton>
      </div>

      <div
        class="my-auto ml2 mr-auto lg:ml0 md:ml0 md:ml3"
        :class="{ '-ml4': isCheckoutPage }"
      >
        <NuxtLinkLocale to="/" class="mr3 w18 flex overflow-hidden md:w-auto xs:w-[5.75rem]" :aria-label="t('general.homepage.label')">
          <UzoLogo class="h8 w-auto text-slate-900 dark:text-white" />
        </NuxtLinkLocale>
      </div>

      <div v-if="!isCheckoutPage" class="mx2 hidden lg:inline-flex">
        <div class="h-full flex grow-1 flex-row items-center justify-start">
          <NuxtLinkLocale
            to="/collections/best-sellers"
            class="relative mx0 h10 w-auto flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-full px2.5 fw600 after:absolute after:inset-0 after:animate-[fade-out-scale-down_.2s_ease_both] after:rounded-full after:bg-slate-100/95 after:will-change-transform after:content-[''] after:-z-1 hover:after:animate-[fade-in-scale-up_.24s_linear_both]"
          >
            <Icon name="i-mdi:star-circle" class="mr1" />
            <span :title="t('pages.title.best_sellers')">{{ t('pages.title.best_sellers') }}</span>

            <span class="absolute rd-t-3xl rd-bl-md rd-br-3xl bg-orange-500 pb0.25 pl1.25 pr1.5 pt0.5 text-xs text-white fw-bold -right-2 -top-1.5">
              {{ t('pages.title.best_sellers_badge') }}
            </span>
          </NuxtLinkLocale>
        </div>

        <div class="h-full flex grow-1 flex-row items-center justify-start">
          <NuxtLinkLocale
            to="/collections/new-arrivals"
            class="relative mx0 h10 w-auto flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-full px2.5 fw600 after:absolute after:inset-0 after:animate-[fade-out-scale-down_.2s_ease_both] after:rounded-full after:bg-slate-100/95 after:will-change-transform after:content-[''] after:-z-1 hover:after:animate-[fade-in-scale-up_.24s_linear_both]"
          >
            <span :title="t('pages.title.new_arrivals')">{{ t('pages.title.new_arrivals') }}</span>
          </NuxtLinkLocale>
        </div>
      </div>
    </template>

    <template v-if="!isCheckoutPage" #center>
      <form class="mx4" @submit.prevent="submitSearch">
        <label for="searchbar" class="sr-only">{{ t('general.search.label') }}</label>
        <div class="relative w-full items-center">
          <UiInput id="searchbar" v-model="searchQueryInput" class="pl10" />
          <span class="absolute start-0 inset-y-0 flex items-center justify-center px2">
            <Icon name="lucide:search" />
          </span>
        </div>
      </form>
    </template>

    <template #right>
      <div v-if="!isCheckoutPage" class="hidden sm:block">
        <LanguageButton />
      </div>

      <UiButton :aria-label="t('general.search.label')" size="icon" variant="ghost" class="md:hidden" @click="handleNavigationClick">
        <Icon name="lucide:search" class="text-xl" />
      </UiButton>

      <UiButton
        v-if="!isCheckoutPage"
        id="account"
        :aria-label="t('page.account.title')"
        size="icon"
        variant="ghost"
        class="sm:hidden"
        as-child
      >
        <NuxtLinkLocale to="/account">
          <Icon name="lucide:user-round" class="text-xl" />
        </NuxtLinkLocale>
      </UiButton>

      <UiDropdownMenu v-if="!isCheckoutPage" :open="openDropdownMenu" @update:open="onDropdownMenuOpen">
        <UiDropdownMenuTrigger as-child>
          <UiButton id="userAccount" data-testid="userAccount" :aria-label="t('general.navigation.label')" size="icon" variant="ghost" class="hidden sm:inline-flex">
            <Icon name="lucide:user-round" class="text-xl" />
          </UiButton>
        </UiDropdownMenuTrigger>
        <UiDropdownMenuContent>
          <UiDropdownMenuItem v-for="accountNavItem in accountNav" :key="accountNavItem.label" as-child>
            <NuxtLinkLocale :to="accountNavItem.to">
              <Icon v-if="accountNavItem.icon" :name="accountNavItem.icon" class="mr2" />
              {{ accountNavItem.label }}
            </NuxtLinkLocale>
          </UiDropdownMenuItem>
          <UiDropdownMenuSeparator />
          <UiDropdownMenuItem @click="authLogout">
            {{ t('form.sign_out.label') }}
          </UiDropdownMenuItem>
        </UiDropdownMenuContent>
      </UiDropdownMenu>

      <div class="relative flex items-center">
        <UiButton data-testid="cart" :aria-label="t('cart.label')" size="icon" variant="ghost" @click="handleCartClick">
          <Icon name="lucide:shopping-cart" class="text-xl" />
        </UiButton>

        <ClientOnly>
          <span
            v-if="totalQuantity > 0"
            class="pointer-events-none absolute h6 w6 flex items-center justify-center rounded-full bg-orange-500 text-sm text-white -right-2 -top-1"
          >
            {{ totalQuantity }}
          </span>
        </ClientOnly>
      </div>
    </template>

    <template v-if="!isCheckoutPage" #navbar>
      <div class="flex content-center items-center lg:flex lg:py-2 lg:space-x-8">
        <UiButton :aria-label="t('general.navigation.label')" size="icon" variant="ghost" @click="handleNavigationClick">
          <Icon name="lucide:menu" class="text-xl" />
        </UiButton>

        <template v-if="rootCollectionsState.status === 'pending'">
          <NSkeleton v-for="i in 9" :key="i" :style="{ width: `${100 + (i * i + i) % 7 * 20}px` }" pulse />
        </template>
        <template v-else-if="rootCollectionsState.data && rootCollectionsState.data?.length">
          <NuxtLinkLocale
            v-for="collection in rootCollectionsState.data.slice(0, 9)"
            :key="collection.id"
            :to="`/collections/${collection.id}/${collection.slug}`"
            n="sm slate9 dark:slate2"
            class="whitespace-nowrap text-context fw600 n-transition"
          >
            {{ collection.name }}
          </NuxtLinkLocale>
        </template>
      </div>
    </template>

    <template v-if="!isCheckoutPage" #search>
      <div class="w-full">
        <form @submit.prevent="submitSearch">
          <label for="search" class="sr-only">{{ t('general.search.label') }}</label>
          <div class="relative w-full items-center">
            <UiInput id="search" v-model="searchQueryInput" class="pl10" />
            <span class="absolute start-0 inset-y-0 flex items-center justify-center px2">
              <Icon name="lucide:search" />
            </span>
          </div>
        </form>
      </div>
    </template>
  </Header>
</template>
